import React, { useState, useEffect, Fragment } from 'react';
import { Observer } from 'mobx-react';
import { css } from '@emotion/core';
import { Link } from 'react-router-dom';
import {
  Typography,
  Menu,
  MenuItem,
  IconButton,
  Breadcrumbs,
} from '@material-ui/core';
import MoreVertIcon from '@material-ui/icons/MoreVert';
import { format } from 'date-fns';
import { useRootStore } from '../../../stores/BridgeProvider';
import { VoucherStatusEnums } from '../../common/Enums';
import { WideBox } from '../../styles/styles';
import { NumberHandler } from '../../util/NumberHandler';
import VoucherTransactions from './VoucherTransactions';
import VoucherJoyride from '../../joyrides/VoucherJoyride';
import { filterPreference } from '../../util/UtilFunctions';

const VoucherHeaderContainer = css`
  display: flex;
  justify-content: space-between;
  .vhLeft,
  .vhRight {
    display: flex;
    flex-direction: column;
    justify-content: center;
    .vhInfoRow {
      font-size: 0.8rem;
      display: flex;
      margin: 6px 0 3px;
    }
  }
  .vhRight {
    .vhInfoRow {
      align-self: flex-end;
    }
  }
`;

const BreadcrumbContainer = css`
  width: calc(80% + 60px);
  min-width: 360px;
  max-width: 960px;
  display: flex;
  a,
  a:visited,
  a:hover,
  a:active {
    color: inherit;
  }
`;

const VoucherInfoComponent = ({
  voucher,
  voucherTrans,
  setEditMode,
  deepLink,
  setVoucher,
  fetchVoucher,
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const { joyrideStore, authStore } = useRootStore();
  const { stepIndex, run } = joyrideStore;
  const { accountInfo } = authStore;

  const open = Boolean(anchorEl);
  const handleClick = event => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const {
    Amount,
    AmountMapped,
    AmountPaid,
    VoucherDate,
    Description,
    AccountId,
    DueDate,
    ContactName,
    ContactId,
    VoucherNumber,
    VoucherId,
    StatusId,
    VoucherTypeDesc,
    VoucherTypeId,
    IsMapped,
  } = voucher;

  const approveVoucher = async () => {
    try {
      const nPref = filterPreference(
        accountInfo.NotificationPref,
        'Transaction Status',
      );
      await authStore.request.post(`${process.env.REACT_APP_API}/api/voucher`, {
        VoucherId,
        VoucherTypeId,
        VoucherNumber,
        Description,
        VoucherDate,
        Amount,
        ContactId,
        DueDate,
        StatusId: 3,
        AccountId,
        IsHidden: 0,
        NotificationPref: nPref,
        Email: {
          address: accountInfo.Email,
          name: accountInfo.Name.split(' ')[0],
          status: VoucherStatusEnums[StatusId],
        },
      });
      await Promise.resolve(fetchVoucher());
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <Observer
      render={() => {
        return (
          <Fragment>
            <div css={BreadcrumbContainer}>
              <Breadcrumbs
                aria-label="breadcrumb"
                style={{ marginTop: '1.5rem', alignSelf: 'flex-start' }}
              >
                <Link to="/dashboard">Dashboard</Link>
                <Link to="/vouchers">Vouchers</Link>
                <Link to={`/vouchers/${VoucherId}`} aria-current="page">
                  {VoucherNumber.length > 25
                    ? `${VoucherNumber.slice(0, 25)}...`
                    : VoucherNumber}
                </Link>
              </Breadcrumbs>
            </div>
            <WideBox
              style={{ height: 'auto', minHeight: 'auto', marginTop: '5px' }}
            >
              <div style={{ display: 'flex' }}>
                <div style={{ margin: '0 auto' }}>
                  <Typography variant="h2" gutterBottom id="voucherStep2">
                    {VoucherTypeDesc} | {VoucherNumber}
                  </Typography>
                  <Typography variant="h4" gutterBottom>
                    {ContactName}
                  </Typography>
                  {Description && (
                    <Typography variant="p" gutterBottom>
                      {Description}
                    </Typography>
                  )}
                </div>
                <IconButton
                  className="voucherStep1 voucherStep2"
                  onClick={e => handleClick(e)}
                  style={{ padding: 0, height: 'fit-content' }}
                >
                  <MoreVertIcon />
                </IconButton>
              </div>
              <div css={VoucherHeaderContainer}>
                <div className="vhLeft">
                  <div className="vhInfoRow">
                    <span>
                      <b> Mapped Status: &nbsp;</b>
                    </span>
                    <span>{IsMapped ? `${IsMapped}` : 'N/A'}</span>
                  </div>

                  <div className="vhInfoRow">
                    <span>
                      <b> Total: &nbsp;</b>
                    </span>
                    <span>
                      {Amount ? (
                        <NumberHandler num={Amount} float={2} currency="$" />
                      ) : (
                        'N/A'
                      )}
                    </span>
                  </div>

                  <div className="vhInfoRow">
                    <span>
                      <b> Amount Mapped: &nbsp;</b>
                    </span>
                    <span>
                      {' '}
                      {AmountMapped ? (
                        <NumberHandler
                          num={AmountMapped}
                          float={2}
                          currency="$"
                        />
                      ) : (
                        'N/A'
                      )}
                    </span>
                  </div>
                  <div className="vhInfoRow">
                    <span>
                      <b> Amount Paid: &nbsp;</b>
                    </span>
                    <span>
                      {' '}
                      {AmountPaid ? (
                        <NumberHandler
                          num={AmountPaid}
                          float={2}
                          currency="$"
                        />
                      ) : (
                        'N/A'
                      )}
                    </span>
                  </div>
                </div>
                <div className="vhRight">
                  <div className="vhInfoRow">
                    <span>
                      <b> Status: &nbsp;</b>
                    </span>
                    <span>{VoucherStatusEnums[StatusId]}</span>
                  </div>
                  <div className="vhInfoRow">
                    <span>
                      <b> Invoice Date: &nbsp;</b>
                    </span>
                    <span>{format(VoucherDate, 'MM/DD/YYYY')}</span>
                  </div>

                  <div className="vhInfoRow">
                    <span>
                      <b> Due Date: &nbsp;</b>
                    </span>
                    <span>
                      {DueDate ? format(DueDate, 'MM/DD/YYYY') : 'N/A'}
                    </span>
                  </div>
                  <Menu anchorEl={anchorEl} open={open} onClose={handleClose}>
                    <MenuItem onClick={() => setEditMode(true)}>
                      Edit Voucher
                    </MenuItem>
                    {voucher.StatusId < 3 && (
                      <MenuItem onClick={() => approveVoucher()}>
                        Approve Voucher
                      </MenuItem>
                    )}
                    <MenuItem
                      component={Link}
                      style={{ marginRight: '0px' }}
                      to={{
                        pathname: '/deal-mapper',
                        state: { voucherIds: [voucher.VoucherId] },
                      }}
                    >
                      Map Voucher
                    </MenuItem>
                    {deepLink !== '#' && (
                      <MenuItem>
                        <a
                          style={{ color: '#263238', textDecoration: 'none' }}
                          href={deepLink}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          Go to Xero
                        </a>
                      </MenuItem>
                    )}
                  </Menu>
                </div>
              </div>
            </WideBox>
            <WideBox
              style={{ height: 'auto', minHeight: 'auto' }}
              showoverflow={1}
            >
              <VoucherTransactions
                VoucherId={VoucherId}
                VoucherType={VoucherTypeDesc}
                VoucherTypeId={VoucherTypeId}
                StatusId={StatusId}
                setVoucher={setVoucher}
              />
            </WideBox>
            <VoucherJoyride />
          </Fragment>
        );
      }}
    />
  );
};

export default VoucherInfoComponent;
